import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Image,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Spinner,
} from "@chakra-ui/react";
import { FiUpload, FiImage, FiCheckCircle } from "react-icons/fi";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import Card from "components/card/Card";
import { PrimayButton } from "components/customs/buttons";
import { uploadImageThunk, fetchActiveBackgroundImageThunk } from "store/slices/imageUpload";
import { useDispatch } from "react-redux";
import { BACKEND_BASE_URL } from "lib/constants";

export default function ImageUpload() {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isActiveImage, setIsActiveImage] = useState(false); // Track if showing server image

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const dropzoneBg = useColorModeValue("gray.50", "gray.700");
  const textColor = useColorModeValue("navy.700", "white");
  const brandColor = useColorModeValue("brand.500", "brand.400");

  // Helper function to get the correct image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    // If it's a data URL (local file preview), return as is
    if (url.startsWith('data:')) return url;
    // If it's already a full URL (starts with http), return as is
    if (url.startsWith('http')) return url;
    // Otherwise, prepend BACKEND_BASE_URL
    return `${BACKEND_BASE_URL}/${url}`;
  };

  // Fetch active background image on mount and set as preview
  useEffect(() => {
    const fetchActiveImage = async () => {
      const result = await dispatch(fetchActiveBackgroundImageThunk());
      if (result.payload?.imageUrl) {
        setPreviewUrl(result.payload.imageUrl);
        setIsActiveImage(true); // Mark as active/uploaded image
      }
    };
    fetchActiveImage();
  }, [dispatch]);

  // Validate file type and size
  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only PNG, JPG, GIF, and WEBP are allowed.');
      return false;
    }

    if (file.size > maxSize) {
      toast.error('File is too large. Maximum size is 5MB.');
      return false;
    }

    return true;
  };

  // Validate image dimensions
  const validateDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        const width = img.width;
        const height = img.height;

        if (width === 1920 && height === 1080) {
          resolve({ valid: true });
        } else {
          resolve({ 
            valid: false, 
            message: `Invalid dimensions: ${width}x${height}. Required: 1920x1080` 
          });
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ valid: false, message: 'Failed to load image' });
      };

      img.src = url;
    });
  };

  // Handle file selection
  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type and size
    if (!validateFile(file)) {
      return;
    }

    // Validate image dimensions
    const dimensionCheck = await validateDimensions(file);
    if (!dimensionCheck.valid) {
      toast.error(dimensionCheck.message);
      return;
    }

    setSelectedFile(file);
    setIsActiveImage(false); // Mark as user-selected image

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle click to upload
  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle upload to server
  const handleUpload = async () => {
    console.log('🚀 handleUpload called');
    console.log('selectedFile:', selectedFile);
    
    if (!selectedFile) {
      console.log('❌ No selectedFile, aborting');
      toast.error("Please select an image first");
      return;
    }

    console.log('✅ Starting upload for:', selectedFile.name);
    setIsUploading(true);
    
    try {
      console.log('📤 Dispatching uploadImageThunk...');
      const result = await dispatch(uploadImageThunk({ file: selectedFile }));
      console.log('📥 Upload result:', result);
      
      if (result.payload?.success) {
        console.log('✅ Upload successful');
        toast.success("Image uploaded successfully!");
        setSelectedFile(null);
        
        // Refresh and show active image
        console.log('🔄 Fetching active image...');
        const activeResult = await dispatch(fetchActiveBackgroundImageThunk());
        console.log('Active image result:', activeResult);
        
        if (activeResult.payload?.imageUrl) {
          setPreviewUrl(activeResult.payload.imageUrl);
          setIsActiveImage(true); // Mark as active image after upload
        }
      } else {
        console.log('❌ Upload failed, no success in payload');
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      toast.error("Failed to upload image");
    } finally {
      console.log('🏁 Upload process finished');
      setIsUploading(false);
    }
  };

  // Clear selected file - only for user-selected images
  const handleClearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsActiveImage(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex direction="column" maxW="800px" mx="auto">
        {/* Upload Section */}
        <Card p="30px" mb={6}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Text
                  fontSize="2xl"
                  fontWeight="700"
                  color={textColor}
                  mb="10px"
                >
                  Background Image
                </Text>
                <Text fontSize="md" color="gray.500">
                  Drag and drop or click to select an image
                </Text>
                <Text fontSize="sm" color="orange.500" mt="5px" fontWeight="600">
                  Required dimensions: 1920 x 1080 pixels
                </Text>
              </Box>

              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />

              {/* Dropzone / Preview Area */}
              <Box
                position="relative"
                border="2px dashed"
                borderColor={
                  previewUrl
                    ? "transparent"
                    : isDragging
                    ? brandColor
                    : borderColor
                }
                borderRadius="20px"
                overflow="hidden"
                minH="400px"
                transition="all 0.2s"
                role="group"
              >
                {!previewUrl ? (
                  // Empty State - Show Upload Area
                  <Box
                    bg={isDragging ? brandColor + "10" : dropzoneBg}
                    p="40px"
                    textAlign="center"
                    cursor="pointer"
                    h="400px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    onClick={handleClickUpload}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    _hover={{
                      transform: "scale(1.01)",
                      borderColor: brandColor,
                    }}
                  >
                    <VStack spacing={4}>
                      <Icon
                        as={FiUpload}
                        w="60px"
                        h="60px"
                        color={isDragging ? brandColor : "gray.400"}
                      />
                      <VStack spacing={1}>
                        <Text fontSize="lg" fontWeight="600" color={textColor}>
                          {isDragging
                            ? "Drop the image here"
                            : "Drop your image here"}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          or click to browse
                        </Text>
                      </VStack>
                      <VStack spacing={1}>
                        <Text fontSize="xs" color="gray.400">
                          PNG, JPG, GIF, WEBP (max 5MB)
                        </Text>
                        <Text fontSize="xs" color="orange.500" fontWeight="600">
                          Dimensions: 1920 x 1080 pixels
                        </Text>
                      </VStack>
                    </VStack>
                  </Box>
                ) : (
                  // Preview State - Show Image with Hover Overlay
                  <>
                    {/* Image Preview */}
                    <Image
                      src={getImageUrl(previewUrl)}
                      alt="Preview"
                      w="100%"
                      h="400px"
                      objectFit="cover"
                    />

                    {/* Close Button (Top Right) - Only show for user-selected images */}
                    {!isActiveImage && (
                      <Box
                        position="absolute"
                        top="15px"
                        right="15px"
                        bg="white"
                        borderRadius="50%"
                        w="36px"
                        h="36px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        boxShadow="lg"
                        transition="all 0.2s"
                        _hover={{
                          transform: "scale(1.1)",
                          bg: "red.50",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearSelection();
                        }}
                        zIndex="2"
                      >
                        <Icon as={MdClose} w="20px" h="20px" color="red.500" />
                      </Box>
                    )}

                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      bg="blackAlpha.700"
                      opacity="0"
                      cursor="pointer"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      transition="opacity 0.3s"
                      _groupHover={{
                        opacity: 1,
                      }}
                      onClick={handleClickUpload}
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <VStack spacing={3}>
                        <Icon
                          as={FiUpload}
                          w="50px"
                          h="50px"
                          color="white"
                        />
                        <Text
                          fontSize="lg"
                          fontWeight="600"
                          color="white"
                        >
                          Click to replace image
                        </Text>
                        <Text fontSize="sm" color="whiteAlpha.800">
                          or drag and drop
                        </Text>
                      </VStack>
                    </Box>

                    {/* Image Info Bar (Bottom) */}
                    <Box
                      position="absolute"
                      bottom="0"
                      left="0"
                      right="0"
                      bg="blackAlpha.800"
                      p="15px"
                      backdropFilter="blur(10px)"
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing={0}>
                          <Text
                            fontSize="sm"
                            color="white"
                            fontWeight="600"
                            isTruncated
                            maxW="300px"
                          >
                            {selectedFile?.name || "Active Background Image"}
                          </Text>
                          <Text fontSize="xs" color="whiteAlpha.700">
                            {selectedFile 
                              ? `${(selectedFile?.size / 1024).toFixed(2)} KB`
                              : "Currently active background"
                            }
                          </Text>
                        </VStack>
                        <HStack>
                          <Icon as={FiCheckCircle} color="green.300" />
                          <Text fontSize="xs" color="green.300" fontWeight="600">
                            {selectedFile ? "Ready" : "Active"}
                          </Text>
                        </HStack>
                      </HStack>
                    </Box>
                  </>
                )}
              </Box>

              {/* Upload Button */}
              <PrimayButton
                label={
                  isUploading ? (
                    <HStack spacing={2}>
                      <Spinner size="sm" />
                      <Text>Uploading...</Text>
                    </HStack>
                  ) : (
                    "Save Image"
                  )
                }
                disabled={!selectedFile || isUploading}
                onClick={handleUpload}
                sx={{
                  width: "100%",
                  height: "50px",
                  fontSize: "16px",
                }}
              />
            </VStack>
          </Card>
      </Flex>
    </Box>
  );
}
